import MusicUploadDialog from "../../presentations/MusicUploadDialog";
import { ApplicationStatus, CategoryTypes } from "../../../interfaces/enums";
import { ApplicationWithId } from "../../../interfaces/IApplications";
import { Box, Button } from "@mui/material";
import { CategoryWithId } from "../../../interfaces/ICategories";
import { Delete, Edit, MusicNote } from "@mui/icons-material";
import { isRegistrationOpen } from "../../../utils/utils";
import { useContestContext } from "../../../contexts/ContestContext";
import { useMusicUploadPermission } from "../../../hooks/useMusicUploadPermission";
import { useNavigationHelper } from "../../../hooks/useNavigationHelper";
import { usePresentationMusic } from "../../../hooks/usePresentationMusic";
import { useState } from "react";

interface PresentationActionsProps {
  application: ApplicationWithId;
  presentation: any;
  onEditPresentation: (presentation: any) => void;
  onDeletePresentation: (presentation: any) => void;
  category: CategoryWithId;
}

export default function PresentationActions({
  application,
  presentation,
  onEditPresentation,
  onDeletePresentation,
  category,
}: PresentationActionsProps) {
  const { contest } = useContestContext();
  const { canUploadMusic } = useMusicUploadPermission(category, contest);
  const [musicDialogOpen, setMusicDialogOpen] = useState(false);
  
  // Użyj hooka do sprawdzania czy muzyka istnieje (aktualizuje się w czasie rzeczywistym)
  const { music } = usePresentationMusic(
    application.contestId,
    presentation.categoryId,
    presentation.id || ""
  );
  
  // Sprawdź czy muzyka istnieje i jest gotowa
  const hasMusic = music !== null && music.status === "ready";

  // Przyciski edycji i usuwania są widoczne tylko dla DRAFT
  const canEdit = application.status === ApplicationStatus.DRAFT && isRegistrationOpen(contest || undefined);
  
  // Przycisk muzyki jest widoczny dla DRAFT, SENT i APPROVED
  const canManageMusic = canUploadMusic && (
    application.status === ApplicationStatus.DRAFT ||
    application.status === ApplicationStatus.SENT ||
    application.status === ApplicationStatus.APPROVED
  );

  // Jeśli nie można ani edytować, ani zarządzać muzyką, ukryj cały komponent
  if (!canEdit && !canManageMusic) {
    return null;
  }

  // Handler dla middle-click i left-click
  const handleEditClick = (event: React.MouseEvent) => {
    if (event.button === 1) {
      // Middle-click (scroll wheel) - otwórz w nowej karcie
      event.preventDefault();
      const url = `/contests/${application.contestId}/applications/${application.id}/categories/${presentation.categoryId}/presentations/${presentation.id}`;
      window.open(url, "_blank");
    } else if (event.button === 0) {
      // Left-click - normalna nawigacja
      onEditPresentation(presentation);
    }
  };

  // Dla kategorii SOLO: ukryj przyciski edycji i usuwania (odbywa się przez główny przycisk kategorii)
  // Ale pokazuj przycisk muzyki, jeśli jest dostępny
  const isSoloCategory = category.type === CategoryTypes.SOLO;

  return (
    <>
      <Box sx={{ display: "flex", gap: 1, flexShrink: 0 }}>
        {/* Dla SOLO: ukryj przyciski edycji i usuwania (odbywa się przez główny przycisk kategorii) */}
        {canEdit && !isSoloCategory && (
          <>
            <Button
              variant="outlined"
              size="small"
              startIcon={<Edit />}
              onClick={handleEditClick}
              onMouseDown={handleEditClick}
              sx={{
                color: "#6366f1",
                borderColor: "#6366f1",
                "&:hover": {
                  backgroundColor: "#f1e7de",
                  borderColor: "#6366f1",
                },
                py: 0.5,
                px: 1.5,
                fontSize: "0.7rem",
              }}
            >
              Edytuj
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<Delete />}
              onClick={() => onDeletePresentation(presentation)}
              sx={{
                color: "#ef4444",
                borderColor: "#ef4444",
                "&:hover": {
                  backgroundColor: "#fef2f2",
                  borderColor: "#ef4444",
                },
                py: 0.5,
                px: 1.5,
                fontSize: "0.7rem",
              }}
            >
              Usuń
            </Button>
          </>
        )}
        {/* TEMPORARILY DISABLED FOR PROD - Music functionality */}
        {/* Przycisk muzyki: dostępny dla wszystkich kategorii (w tym SOLO), jeśli canManageMusic jest true */}
        {canManageMusic && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<MusicNote />}
            onClick={() => setMusicDialogOpen(true)}
            sx={{
              color: hasMusic ? "#10b981" : "#6366f1",
              borderColor: hasMusic ? "#10b981" : "#6366f1",
              "&:hover": {
                backgroundColor: hasMusic ? "#f0fdf4" : "#f1e7de",
                borderColor: hasMusic ? "#10b981" : "#6366f1",
              },
              py: 0.5,
              px: 1.5,
              fontSize: "0.7rem",
            }}
          >
            {hasMusic ? "Muzyka" : "Dodaj muzykę"}
          </Button>
        )}
      </Box>

      {/* Dialog uploadu muzyki */}
      {/* TEMPORARILY DISABLED FOR PROD - Music functionality */}
      {canManageMusic && (
        <MusicUploadDialog
          open={musicDialogOpen}
          onClose={() => setMusicDialogOpen(false)}
          tournamentId={application.contestId}
          categoryId={presentation.categoryId}
          presentationId={presentation.id || ""}
          presentationName={presentation.name}
          hasMusic={hasMusic}
        />
      )}
    </>
  );
}
